import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Pagination from '../../components/Pagination';
import TextInput from '../../components/TextInput';
import SkeletonLoader from '../../components/SkeletonLoader';
import Button from '../../components/Button';
import { jobApi } from '../../api/jobApi';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useToast } from '../../contexts/ToastContext';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  return new Date(dateString).toLocaleDateString('en-US', options);
};

const JobListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [lastPage, setLastPage] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchJobs = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      const response = await jobApi.getJobs(page, perPage, search);
      setJobs(response.data.data);
      setLastPage(response.data.pagination.last_page);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setError('Failed to load jobs. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchJobs, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleProcessJobs = async () => {
    try {
      const response = await jobApi.processJobs();
      addToast(response.data.message, 'success');
      // Refresh the jobs list after processing
      fetchJobs(currentPage, itemsPerPage, debouncedSearchTerm);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to process jobs.';
      addToast(errorMessage, 'error');
    }
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
        <h1 className="text-3xl font-bold text-text-white mb-4 sm:mb-0">Jobs</h1>
        <div className="flex space-x-4">
          <Button
            onClick={handleProcessJobs}
            className="bg-accent-red hover:bg-red-700 text-white"
          >
            Process Jobs
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Jobs"
          id="search-jobs"
          name="search-jobs"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by payload..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Job Name</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Attempts</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Available At</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Created At</th>
                </tr>
              </thead>
              <tbody>
                {jobs.length > 0 ? (
                  jobs.map((job) => (
                    <tr key={job.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{job.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{job.payload.displayName}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{job.attempts}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{formatDate(job.available_at)}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{formatDate(job.created_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-3 px-4 text-center text-sm text-text-gray">No jobs found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
          />
        </>
      )}
    </div>
  );
};

export default JobListPage;